import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import { icon, label, tooltip } from "../../../../config";
import { formatFormDate } from "../../../../helper/common";
import { FORM_TABS, LAST_UPDATED_KEYS_BY_TAB } from "../../../Forms/FormsConstant";

/**
 * Renders submitter and last-updated metadata for the task form header.
 * @param {Object} formData - Form submission data
 * @param {Object} formTab - Active tab config (`type`: form | review)
 * @returns {JSX.Element|null}
 * @author Abhishek Dhillon
 */
const renderTaskFormMetadata = (formData, formTab, defaultDetails) => {
  if (!formData?.submitterDetails?.name || !formData?.createdAt) {
    return null;
  }
  let lastUpdatedByDisplayName = "";
  let lastUpdatedAt = "";
  if (formTab?.type === FORM_TABS?.SUBSECTIONS_OBJ?.type) {
    lastUpdatedByDisplayName = formData?.lastUpdatedByName;
    lastUpdatedAt = formData?.updatedAt;
  } else {
    lastUpdatedByDisplayName = defaultDetails?.assigneeList?.find((assignee) => assignee?.value === formData?.reviewLastUpdatedBy)?.label;
    lastUpdatedAt = formData?.reviewLastUpdatedAt;
  }

  return (
    <div className='form-metadata d-flex align-items-center'>
      {formTab?.type === FORM_TABS?.SUBSECTIONS_OBJ?.type && (
        <div className='metadata-item mr-2'>
          {label.SUBMITTED_BY} {formData?.submitterDetails?.name} {label.ON} {formatFormDate(formData?.createdAt)}
          {lastUpdatedByDisplayName ? ", " : ""}
        </div>
      )}
      {lastUpdatedByDisplayName && lastUpdatedAt && (
        <div className='metadata-item'>
          {label.LAST_UPDATED_BY} {lastUpdatedByDisplayName} {label.ON} {formatFormDate(lastUpdatedAt)}
        </div>
      )}
    </div>
  );
};

/**
 * Task Form Header Component - displays the form title, metadata, and action buttons
 * @param {Object} props
 * @param {Object} props.formData - Form submission data
 * @param {Function} props.onEditForm - Callback to show edit form
 * @param {Function} props.onClose - Callback to close the popup
 * @param {Object} props.formTab - Form tab data
 * @returns {JSX.Element} Header component
 * @author Abhishek Dhillon
 */
const TaskFormHeader = ({ formData, onEditForm, onClose, formTab }) => {
  const { user } = useSelector((state) => state.auth);
  const { task } = useSelector((state) => state.taskSidebar);
  const CurrentProject = task?.CurrentProject;
  const { allProjectsList } = useSelector((state) => state.tasks.defaultDetails);
  const currentProjectConfig = allProjectsList?.find((project) => project.ProjectId === CurrentProject);
  const { owners, collaborators } = currentProjectConfig || {};
  const currentProjectTeam = `${owners || ""},${collaborators || ""}`.split(",");
  const isCurrentProjectTeamMember = currentProjectTeam.includes(String(user?.id));
  const { TeamMangers } = task;
  const canEditForm = Boolean(formData?.formId && formData?.formId?.isEditable && !(task?.IsTaskComplete || task?.IsTaskClosed));
  const editFormDisabled = !TeamMangers?.includes(user?.id);
  const editReviewDisabled = useMemo(() => {
    if (String(user?.id) === String(formData?.submitterDetails?.id)) return true;
    if (isCurrentProjectTeamMember) return false;
    return true;
  }, [user?.id, formData?.submitterDetails?.id, isCurrentProjectTeamMember]);

  const { defaultDetails } = useSelector((state) => state.tasks);
  const editButtonTitle = useMemo(() => {
    if (formTab?.hostSection === FORM_TABS?.SUBSECTIONS_OBJ?.hostSection) {
      if (editFormDisabled) {
        return tooltip.EDITABLE_BY_MANAGERS_ONLY(user?.operationalTeamName || label.TEAM);
      } else {
        return tooltip.EDIT_FORM;
      }
    } else {
      if (editReviewDisabled) {
        return tooltip.EDITABLE_BY_CURRENT_TEAM_MEMBERS_ONLY;
      } else {
        return tooltip.EDIT_REVIEW;
      }
    }
  }, [formTab?.hostSection, editFormDisabled, editReviewDisabled, user?.operationalTeamName]);

  return (
    <div className='dt-popup-header task-form-header d-flex justify-content-between align-items-center pt-1 pb-1 pl-3 pr-3'>
      <div className='dt-popup-title task-form-header-text p-2'>
        <div className='d-flex align-items-center'>
          <span className='me-2'>{icon.FORMS}</span>
          <span>{label.RESPONSE}</span>
          {formData?.title && <span className='text-muted ms-2'> - {formData?.title}</span>}
          {!formData?.title && <span className='text-muted ms-2'> - {formData?.content?.title}</span>}
        </div>
        {renderTaskFormMetadata(formData, formTab, defaultDetails)}
      </div>
      <div className='d-flex gap-2'>
        <div title={editButtonTitle}>
          {formTab?.hostSection === FORM_TABS?.SUBSECTIONS_OBJ?.hostSection && canEditForm && (
            <Button
              id='task-form-edit-button'
              size='small'
              onClick={onEditForm}
              type='button'
              fillMode={"outline"}
              themeColor={"primary"}
              disabled={editFormDisabled}
              className={`btn btn-tertiary ${editFormDisabled ? "disabled" : ""}`}>
              {label.EDIT}
            </Button>
          )}
          {formTab?.hostSection === FORM_TABS?.HIDDEN_SUBSECTIONS_OBJ?.hostSection && (
            <Button
              id='task-form-edit-button'
              size='small'
              onClick={onEditForm}
              type='button'
              fillMode={"outline"}
              themeColor={"primary"}
              disabled={editReviewDisabled}
              className={`btn btn-tertiary ${editReviewDisabled ? "disabled" : ""}`}>
              {label.EDIT}
            </Button>
          )}
        </div>
        <span id='task-form-popup-close' className='dt-popup-cancel-btn cursor-pointer' title={tooltip.CLOSE_POPUP} onClick={onClose}>
          {icon.CLOSE}
        </span>
      </div>
    </div>
  );
};

export default TaskFormHeader;
